import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import Sidebar from '../../components/sidebar/Sidebar';
import { Button } from '../../components/common/Button';
import { RootState } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import chatService from '../../services/api/chatService';
import aiAgentService from '../../services/api/aiAgentService';
import { AgentWithIcon, FormattedChat, FormattedAgent } from '../../types/chat';
import { Chat } from '../../types';
import { ResponsiveAppShell, TaskyTopBar } from '../../components/tasky';
import { logger } from '../../utils/logger';

// サブスクリプションプランの定義
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: string;
  features: string[];
  isPopular?: boolean;
}

// 使用統計の定義
interface UsageStats {
  messagesCount: number;
  tokensUsed: number;
  conversationsCount: number;
  averageResponseTime: number;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux状態
  const { user } = useSelector((state: RootState) => state.auth);

  // ローカルステート
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [userChats, setUserChats] = useState<Chat[]>([]);
  const [agents, setAgents] = useState<AgentWithIcon[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentWithIcon | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ダミーデータ
  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: '無料プラン',
      price: 0,
      interval: '月',
      features: [
        '1日20メッセージまで',
        '基本AIエージェント',
        '履歴7日間保存'
      ]
    },
    {
      id: 'basic',
      name: 'ベーシック',
      price: 980,
      interval: '月',
      features: [
        '1日100メッセージまで',
        '全AIエージェント',
        '履歴30日間保存',
        '優先サポート'
      ],
      isPopular: true
    },
    {
      id: 'premium',
      name: 'プレミアム',
      price: 2980,
      interval: '月',
      features: [
        'メッセージ無制限',
        '全AIエージェント',
        '履歴無期限保存',
        '優先サポート',
        'カスタムAIエージェント作成'
      ]
    }
  ];

  const usageStats: UsageStats = {
    messagesCount: 328,
    tokensUsed: 24692,
    conversationsCount: 42,
    averageResponseTime: 1.2
  };

  // ユーザー情報の初期化とデータ取得
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setCurrentPlan(user.organization?.plan || 'free');
      
      // チャット一覧を取得
      const fetchChats = async () => {
        try {
          setIsLoading(true);
          const chats = await chatService.getUserChats();
          setUserChats(chats);
        } catch (error) {
          console.error('Failed to fetch user chats:', error);
        } finally {
          setIsLoading(false);
        }
      };
      
      // エージェント一覧を取得
      const fetchAgents = async () => {
        try {
          const agentsData = await aiAgentService.getActiveAgents();
          
          // アイコンを追加して拡張
          const agentsWithIcons: AgentWithIcon[] = agentsData.map(agent => {
            const modelIcons: {[key: string]: string} = {
              'chatgpt': '🤖',
              'claude': '🧠',
              'gemini': '✨'
            };
            
            return {
              ...agent,
              name: agent.title || 'Unknown Agent',
              avatar: `https://via.placeholder.com/40?text=${agent.title?.substring(0, 2) || 'AI'}`,
              capabilities: [],
              icon: modelIcons[agent.model_type || 'chatgpt'] || '🤖'
            };
          });
          
          setAgents(agentsWithIcons);
        } catch (error) {
          console.error('Failed to fetch agents:', error);
        }
      };
      
      fetchChats();
      fetchAgents();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      console.log('ProfilePage: Logging out user');
      
      // ローカルストレージからトークンとリフレッシュトークンを削除
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      
      // Reduxステートをクリア
      await dispatch(logout());
      
      // ログインページにリダイレクト
      console.log('AxiosConfig: Redirecting to login page');
      navigate('/login');
    } catch (error) {
      console.error('ProfilePage: Logout failed:', error);
    }
  };
  
  // エージェント選択処理
  const handleAgentSelect = (agentId: number) => {
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      setSelectedAgent(agent);
    }
  };
  
  // チャット情報を整形
  const formattedChats: FormattedChat[] = userChats.map(chat => ({
    id: chat.id,
    title: chat.title || chat.agent?.title || `チャット #${chat.id}`
  }));

  // エージェント情報を整形（AIAgent型に合わせる）
  const formattedAgents = agents.map(agent => ({
    id: agent.id,
    title: agent.name || agent.title,
    description: agent.description || '',
    model_type: agent.model_type || 'chatgpt',
    prompt_template: agent.prompt_template || '',
    is_active: agent.is_active !== undefined ? agent.is_active : true,
    base_color: agent.base_color || '#FFC107',
    category: agent.category || 'その他',
    // FormattedAgentの互換性のために残す
    name: agent.name,
    avatar: agent.avatar,
    capabilities: agent.capabilities || []
  }));

  const handleSaveProfile = () => {
    // TODO: 名前・メールアドレスの更新 API 未接続（保存されない）
    logger.log('Saving profile:', { name, email });
    setIsEditing(false);
  };

  const handleChangePlan = (planId: string) => {
    // 実際にはAPI呼び出しでプラン変更
    console.log('Changing plan to:', planId);
    setCurrentPlan(planId);
  };

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      chats={formattedChats}
      agents={formattedAgents}
      onSelectAgent={handleAgentSelect}
      selectedAgentId={selectedAgent?.id || null}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = (
    <Sidebar
      onLogout={handleLogout}
      chats={formattedChats}
      agents={formattedAgents}
      onSelectAgent={handleAgentSelect}
      selectedAgentId={selectedAgent?.id || null}
    />
  );

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="プロフィール設定">
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white max-[1024px]:min-w-0 lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title="プロフィール設定"
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />
        <div className="flex-1 bg-white overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          {/* アカウント情報カード */}
          <div className="mb-8 overflow-hidden rounded-lg bg-white shadow-sm max-[640px]:mb-4" data-tutorial="profile-account">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 max-[640px]:items-start max-[640px]:gap-3 max-[640px]:p-4">
              <h2 className="m-0 text-xl font-medium text-dark max-[640px]:text-base">アカウント情報</h2>
              <div>
                <button className="cursor-pointer border-none bg-transparent text-sm font-medium text-primary hover:underline" onClick={() => setIsEditing(!isEditing)}>
                  {isEditing ? 'キャンセル' : '編集'}
                </button>
              </div>
            </div>

            <div className="p-6 max-[640px]:p-4">
              <div className="mb-6 last:mb-0">
                <label htmlFor="name" className="mb-2 block font-medium text-dark">名前</label>
                <input
                  id="name"
                  className="w-full rounded border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:shadow-[0_0_0_2px_rgba(97,111,201,0.1)] focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isEditing}
                />
              </div>

              <div className="mb-6 last:mb-0">
                <label htmlFor="email" className="mb-2 block font-medium text-dark">メールアドレス</label>
                <input
                  id="email"
                  className="w-full rounded border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:shadow-[0_0_0_2px_rgba(97,111,201,0.1)] focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!isEditing}
                />
              </div>

              {/* パスワードの設定・変更はアカウント設定に集約している。
                  ここに入力欄を置いていた時期があるが保存処理が無く、
                  変更したつもりで変わっていない状態になっていた。 */}
              <div className="mb-6 last:mb-0">
                <label className="mb-2 block font-medium text-dark">パスワード</label>
                <a
                  href="/account/settings"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  アカウント設定でパスワードを設定・変更する
                </a>
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end gap-4 border-t border-gray-200 px-6 py-5 max-[640px]:flex-col-reverse max-[640px]:p-4 max-[640px]:[&>button]:min-h-[44px] max-[640px]:[&>button]:w-full">
                <Button onClick={() => setIsEditing(false)}>キャンセル</Button>
                <Button primary onClick={handleSaveProfile}>保存</Button>
              </div>
            )}
          </div>

          {/* サブスクリプションカード */}
          <div className="mb-8 overflow-hidden rounded-lg bg-white shadow-sm max-[640px]:mb-4" data-tutorial="profile-subscription">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 max-[640px]:items-start max-[640px]:gap-3 max-[640px]:p-4">
              <h2 className="m-0 text-xl font-medium text-dark max-[640px]:text-base">サブスクリプション</h2>
              <div>
                <button className="cursor-pointer border-none bg-transparent text-sm font-medium text-primary hover:underline" onClick={() => {}}>請求履歴</button>
              </div>
            </div>

            <div className="p-6 max-[640px]:p-4">
              <p>現在のプラン: <strong>{subscriptionPlans.find(p => p.id === currentPlan)?.name || '無料プラン'}</strong></p>

              <div className="flex flex-wrap gap-6 max-[1024px]:flex-col">
                {subscriptionPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`min-w-[250px] flex-1 rounded-lg p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-[5px] hover:shadow-[0_4px_6px_rgba(0,0,0,0.1)] max-[640px]:min-w-0 max-[640px]:p-4 [&>button]:w-full ${plan.id === currentPlan ? 'border-2 border-primary' : 'border border-gray-200'}`}
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="m-0 text-xl font-bold text-dark">{plan.name}</h3>
                      {plan.isPopular && <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">人気</span>}
                    </div>

                    <div className="mb-4 text-2xl font-bold text-dark">
                      ¥{plan.price.toLocaleString()} <span className="text-sm font-normal text-gray-500">/{plan.interval}</span>
                    </div>

                    <div className="mb-6">
                      <ul className="m-0 list-none p-0">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="mb-2 flex items-center text-dark before:mr-2 before:font-bold before:text-primary before:content-['✓']">{feature}</li>
                        ))}
                      </ul>
                    </div>

                    {plan.id !== currentPlan ? (
                      <Button
                        primary
                        onClick={() => handleChangePlan(plan.id)}
                      >
                        プランを選択
                      </Button>
                    ) : (
                      <Button disabled>現在のプラン</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 使用統計カード */}
          <div className="mb-8 overflow-hidden rounded-lg bg-white shadow-sm max-[640px]:mb-4" data-tutorial="profile-usage-stats">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 max-[640px]:items-start max-[640px]:gap-3 max-[640px]:p-4">
              <h2 className="m-0 text-xl font-medium text-dark max-[640px]:text-base">使用統計</h2>
            </div>

            <div className="p-6 max-[640px]:p-4">
              <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 max-[640px]:grid-cols-1">
                <div className="rounded-lg bg-primary/5 p-5 text-center">
                  <h4 className="mb-2 mt-0 text-sm font-medium text-gray-500">送信メッセージ数</h4>
                  <div className="text-2xl font-bold text-dark">{usageStats.messagesCount}</div>
                </div>

                <div className="rounded-lg bg-primary/5 p-5 text-center">
                  <h4 className="mb-2 mt-0 text-sm font-medium text-gray-500">クレジット使用量</h4>
                  <div className="text-2xl font-bold text-dark">{usageStats.tokensUsed.toLocaleString()}</div>
                </div>

                <div className="rounded-lg bg-primary/5 p-5 text-center">
                  <h4 className="mb-2 mt-0 text-sm font-medium text-gray-500">会話数</h4>
                  <div className="text-2xl font-bold text-dark">{usageStats.conversationsCount}</div>
                </div>

                <div className="rounded-lg bg-primary/5 p-5 text-center">
                  <h4 className="mb-2 mt-0 text-sm font-medium text-gray-500">平均応答時間</h4>
                  <div className="text-2xl font-bold text-dark">{usageStats.averageResponseTime}秒</div>
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    </ResponsiveAppShell>
  );
};

export default ProfilePage;
